/**
 * Workspace telemetry: neat in-code shape plus the opaque wire shape used by
 * POST /challenges/{id}/sync. This file is the only place that knows the
 * letters and integer codes. Obscurity, not security.
 */
export type TelemetryEventType =
  | 'paste' | 'drop' | 'inject'
  | 'active'
  | 'blur' | 'focus'
  | 'multi_tab'
  | 'run'
  | 'save' | 'submit'

/** Types a client may report; everything else (save, submit, sql run) is server-originated. */
export const CLIENT_EVENT_TYPES: ReadonlySet<TelemetryEventType> = new Set<TelemetryEventType>(['paste', 'drop', 'inject', 'active', 'blur', 'focus', 'multi_tab'])

export type TelemetrySurface = 'sql' | 'notebook' | 'solution' | 'notes'

export type TelemetryEvent = {
  t: number
  type: TelemetryEventType
  surface?: TelemetrySurface
  len?: number
  lines?: number
  replacedAll?: boolean
  internal?: boolean
  preview?: string
  pauseMs?: number
  awayMs?: number
  /** Typing stretch length (type 'active'); len holds the keystrokes. */
  durationMs?: number
  error?: string
}

/** Opaque wire form. Keys: t time, k kind, s surface, n len, l lines, r replacedAll, i internal, p preview, q pauseMs, a awayMs, d durationMs, e error. */
export type WireEvent = {
  t: number
  k: number
  s?: number
  n?: number
  l?: number
  r?: number
  i?: number
  p?: string
  q?: number
  a?: number
  d?: number
  e?: string
}

const TYPE_CODES: Record<TelemetryEventType, number> = {
  paste: 1, drop: 2, inject: 3, active: 4, blur: 5, focus: 6, multi_tab: 7, run: 8, save: 9, submit: 10,
}
const SURFACE_CODES: Record<TelemetrySurface, number> = { sql: 1, notebook: 2, solution: 3, notes: 4 }

const invert = <K extends string>(m: Record<K, number>): Map<number, K> =>
  new Map((Object.entries(m) as [K, number][]).map(([k, v]) => [v, k]))
const TYPES_BY_CODE = invert(TYPE_CODES)
const SURFACES_BY_CODE = invert(SURFACE_CODES)

export function encodeTelemetryEvent(e: TelemetryEvent): WireEvent {
  const w: WireEvent = { t: e.t, k: TYPE_CODES[e.type] }
  if (e.surface !== undefined) w.s = SURFACE_CODES[e.surface]
  if (e.len !== undefined) w.n = e.len
  if (e.lines !== undefined) w.l = e.lines
  if (e.replacedAll !== undefined) w.r = e.replacedAll ? 1 : 0
  if (e.internal !== undefined) w.i = e.internal ? 1 : 0
  if (e.preview !== undefined) w.p = e.preview
  if (e.pauseMs !== undefined) w.q = e.pauseMs
  if (e.awayMs !== undefined) w.a = e.awayMs
  if (e.durationMs !== undefined) w.d = e.durationMs
  if (e.error !== undefined) w.e = e.error
  return w
}

export function decodeTelemetryEvent(w: WireEvent): TelemetryEvent | null {
  const type = TYPES_BY_CODE.get(w.k)
  if (type === undefined) return null
  const e: TelemetryEvent = { t: w.t, type }
  const surface = w.s === undefined ? undefined : SURFACES_BY_CODE.get(w.s)
  if (surface !== undefined) e.surface = surface
  if (w.n !== undefined) e.len = w.n
  if (w.l !== undefined) e.lines = w.l
  if (w.r !== undefined) e.replacedAll = w.r === 1
  if (w.i !== undefined) e.internal = w.i === 1
  if (w.p !== undefined) e.preview = w.p
  if (w.q !== undefined) e.pauseMs = w.q
  if (w.a !== undefined) e.awayMs = w.a
  if (w.d !== undefined) e.durationMs = w.d
  if (w.e !== undefined) e.error = w.e
  return e
}
